import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

export interface IPlayerData {
  duration?: number;
  totalDuration?: number;
  loadedTime?: number;
  isHalf?: boolean;
  canForward?: boolean;
  canBackward?: boolean;
  event?: Record<string, any> | null;
  isReady?: boolean;
  error?: { message: string } | null;
  stats?: {
    hits: number;
    secondsListened: number;
  };
}

export interface IPlayerSettings {
  url?: string;
  drm3key?: string;
  isPlaying?: boolean;
  volume?: number;
  playbackRate?: number;
  muted?: boolean;
  isContentsOpen?: boolean;
  downloadForbidden?: boolean;
  readOnly?: boolean;
  isAnon?: boolean;
  isChapterLoading?: boolean;
  contentsData?: IContents[];
  title?: string;
  timeStart?: number;
}

export interface IContents {
  fileId: number;
  title: string;
  url: string;
  duration: number;
  isDownloadForbidden?: boolean;
}

export interface IPlayer {
  currentTime: number;
  playerSettings: IPlayerSettings;
  playerData: IPlayerData;
}

const initialState: IPlayer = {
  playerSettings: {
    url: "",
    drm3key: "",
    isPlaying: false,
    volume: 0.5,
    playbackRate: 1,
    muted: false,
    isContentsOpen: false,
    downloadForbidden: false,
    readOnly: false,
    isAnon: true,
    isChapterLoading: false,
    contentsData: [],
  },
  currentTime: 0,
  playerData: {
    error: null,
    event: null,
    isReady: false,
    loadedTime: 0,
    duration: 0,
    totalDuration: 0,
    isHalf: false,
    canBackward: false,
    canForward: true,
    stats: {
      hits: 0,
      secondsListened: 0,
    },
  },
};

export const playerSlice = createSlice({
  name: "player",
  initialState,
  reducers: {
    setTimeStart: (state: IPlayer, action: PayloadAction<number>) => {
      state.playerSettings.timeStart = action.payload;
    },
    setUrl: (state: IPlayer, action: PayloadAction<string>) => {
      state.playerSettings.url = action.payload;
    },
    setVolume: (state: IPlayer, action: PayloadAction<number>) => {
      state.playerSettings.volume = action.payload;
    },
    setPlaybackRate: (state: IPlayer, action: PayloadAction<number>) => {
      state.playerSettings.playbackRate = action.payload;
    },
    setPlaying: (state: IPlayer, action: PayloadAction<boolean>) => {
      state.playerSettings.isPlaying = action.payload;
      state.playerData.error = null;
    },
    setCurrentTime: (state: IPlayer, action: PayloadAction<number>) => {
      state.currentTime = action.payload;
    },
    setMuted: (state: IPlayer, action: PayloadAction<boolean>) => {
      state.playerSettings.muted = action.payload;
    },
    setDownloadForbidden: (state: IPlayer, action: PayloadAction<boolean>) => {
      state.playerSettings.downloadForbidden = action.payload;
    },
    setReadOnly: (state: IPlayer, action: PayloadAction<boolean>) => {
      state.playerSettings.readOnly = action.payload;
    },
    setContentsOpen: (state: IPlayer, action: PayloadAction<boolean>) => {
      state.playerSettings.isContentsOpen = action.payload;
    },
    setContentsData: (state: IPlayer, action: PayloadAction<IContents[]>) => {
      state.playerSettings.contentsData = action.payload;
    },
    setPlayerError(
      state: IPlayer,
      action: PayloadAction<{ message: string } | null>
    ) {
      state.playerData.error = action.payload ?? null;
    },
    setPlayerEvent: (
      state: IPlayer,
      action: PayloadAction<Record<string, any> | null>
    ) => {
      state.playerData.event = action.payload;
    },
    setPlayerReady: (state: IPlayer, action: PayloadAction<boolean>) => {
      state.playerData.isReady = action.payload;
    },
    setPlayerSettings: (
      state: IPlayer,
      action: PayloadAction<IPlayerSettings>
    ) => {
      for (const key in action.payload) {
        const setting = key as keyof IPlayerSettings;
        const value = action.payload[setting];
        (state.playerSettings[setting] as typeof value) = value;
      }
    },
    setPlayerData: (state: IPlayer, action: PayloadAction<IPlayerData>) => {
      for (const key in action.payload) {
        const typedKey = key as keyof IPlayerData;
        const value = action.payload[typedKey];
        (state.playerData[typedKey] as typeof value) = value;
      }
    },
    setPlayerStats: (
      state: IPlayer,
      action: PayloadAction<{ hits: number; secondsListened: number }>
    ) => {
      state.playerData.stats = action.payload;
    },
    setTotalDuration: (state: IPlayer, action: PayloadAction<number>) => {
      state.playerData.totalDuration = action.payload;
    },
  },
});

export const {
  setPlayerData,
  setPlayerError,
  setPlayerEvent,
  setPlayerReady,
  setTimeStart,
  setContentsOpen,
  setPlaying,
  setCurrentTime,
  setUrl,
  setVolume,
  setPlaybackRate,
  setMuted,
  setPlayerSettings,
  setDownloadForbidden,
  setReadOnly,
  setContentsData,
  setPlayerStats,
  setTotalDuration,
} = playerSlice.actions;

export default playerSlice.reducer;
